import { formatDate } from '@angular/common';
import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BondsServices } from 'src/app/services/bonds.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { BusinessService } from 'src/app/services/business.service';
import { CategoriasService } from 'src/app/services/categorias.service';
import { PanelTitleService } from 'src/app/services/panel-title.service';
import { AppSettings } from 'src/app/app-settings';

@Component({
  selector: 'app-bond-form',
  templateUrl: './bond-form.component.html',
  styleUrls: ['./bond-form.component.scss']
})
export class BondFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isEdit: boolean = false;
  bondId: number | null = null;
  loaded: boolean = false;
  saving: boolean = false;
  saveError: string | null = null;

  business: any;
  companyEvents: any[] = [];
  filteredEvents: any[] = [];
  private eventsLoading: boolean = false;
  selectedEventIds: number[] = [];
  selectedSessionIds: number[] = [];
  searchTerm: string = '';
  eventCategoryFilter: number | null = null;
  categoryDropdownOpen: boolean = false;
  eventCategories: any[] = [];

  allChannelsEnabled: boolean = true;
  selectedChannelIds: number[] = [];

  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';

  startsAtDate: Date | null = null;
  startsAtTime: string | null = null;
  endsAtDate: Date | null = null;
  endsAtTime: string | null = null;

  // Precio por evento y nivel (fixed_price): una fila por sesión
  passGrid: { columns: any[]; rows: any[] } | null = null;
  gridLoading: boolean = false;
  gridError: string | null = null;
  /** Precio de cada celda: [session_id][clave de columna]; null = ese nivel no entra en el abono */
  priceCells: { [sessionId: number]: { [columnKey: string]: number | null } } = {};
  bulkPrice: number | null = null;
  private gridRequestKey: string | null = null;
  private gridSub: Subscription;

  private sub: Subscription;
  private businessSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private bondsService: BondsServices,
    private apiBusiness: ApiBusinessService,
    private businessService: BusinessService,
    private categoriasService: CategoriasService,
    private panelTitleService: PanelTitleService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.categoryDropdownOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.category-dropdown')) {
        this.categoryDropdownOpen = false;
      }
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadCategories();

    this.business = JSON.parse(localStorage.getItem('business') || '{}');

    this.sub = this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEdit = true;
        this.bondId = +params['id'];
        this.loadBond(this.bondId);
      } else {
        this.loadCompanyEvents();
      }
    });

    this.businessSub = this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value && this.business?.id) {
          this.loadCompanyEvents();
        }
      }
    });
  }

  initForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      bond_type: ['fixed', Validators.required],
      fixed_price: [false],
      discount_value: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
      min_events: [null],
      single_seat: [false],
      starts_at: [null],
      ends_at: [null],
    });
  }

  get allowedChannels(): number[] {
    if (this.allChannelsEnabled) return [1, 2, 3, 4, 5];
    return [...this.selectedChannelIds];
  }

  hasBusinessChannel(...names: string[]): boolean {
    const active: any[] = this.business?.tpv_info?.sales_channel || [];
    if (active.length === 0) return false;
    return names.some(n => active.some((sc: any) => sc.name?.toLowerCase() === n.toLowerCase()));
  }

  isChannelSelected(channelId: number): boolean {
    return this.selectedChannelIds.includes(channelId);
  }

  toggleChannel(channelId: number) {
    const idx = this.selectedChannelIds.indexOf(channelId);
    if (idx === -1) {
      this.selectedChannelIds.push(channelId);
    } else {
      this.selectedChannelIds.splice(idx, 1);
    }
  }

  toggleTaquilla() {
    this.selectedChannelIds = [2, 3];
  }

  toggleAllChannels(enabled: boolean) {
    this.allChannelsEnabled = enabled;
    if (enabled) {
      this.selectedChannelIds = [];
    }
  }

  get selectedWebChannel(): boolean {
    return this.selectedChannelIds.includes(1);
  }

  get selectedTaquilla(): boolean {
    return this.selectedChannelIds.includes(2) && this.selectedChannelIds.includes(3);
  }

  toggleWebChannel() {
    this.selectedChannelIds = [1, 4, 5];
  }

  loadBond(id: number) {
    this.bondsService.getPassById(id).subscribe({
      next: (res) => {
        const bond = res?.discount || res?.data || res;
        this.panelTitleService.setSubtitle(bond.name);
        const channels: number[] = bond.allowed_channels || [];
        this.allChannelsEnabled = !channels || channels.length === 0;
        this.selectedChannelIds = channels.length > 0 ? [...channels] : [];
        const fixedPrices = !bond.min_events && !!bond.fixed_price;
        this.form.patchValue({
          name: bond.name,
          bond_type: bond.min_events ? 'flexible' : 'fixed',
          fixed_price: fixedPrices,
          discount_value: fixedPrices ? null : bond.discount_value,
          min_events: bond.min_events || null,
          single_seat: !bond.min_events && !!bond.single_seat,
          starts_at: bond.starts_at ? formatDate(new Date(bond.starts_at), 'yyyy-MM-ddTHH:mm', 'es') : null,
          ends_at: bond.ends_at ? formatDate(new Date(bond.ends_at), 'yyyy-MM-ddTHH:mm', 'es') : null,
        });
        this.updateDiscountValidators();
        if (bond.starts_at) {
          this.startsAtDate = new Date(bond.starts_at);
          this.startsAtTime = formatDate(this.startsAtDate, 'HH:mm', 'es');
        }
        if (bond.ends_at) {
          this.endsAtDate = new Date(bond.ends_at);
          this.endsAtTime = formatDate(this.endsAtDate, 'HH:mm', 'es');
        }
        this.selectedEventIds = [...new Set((bond.pass_items || []).map((item: any) => item.event?.id || item.event_id))] as number[];
        this.selectedSessionIds = (bond.pass_items || []).reduce((acc: number[], item: any) => {
          const sid = item.session_id ?? null;
          if (sid && !acc.includes(sid)) acc.push(sid);
          return acc;
        }, []);
        this.loadCompanyEvents();
      },
      error: () => {
        this.router.navigate(['/panel-control/bonds']);
      }
    });
  }

  loadCompanyEvents() {
    if (!this.business?.id || this.eventsLoading) return;
    this.eventsLoading = true;

    const today = new Date().toISOString().split('T')[0];
    this.businessService.getEventsCompanyFilter(this.business.id, 1, '', null, [], false, true, null, true, true, null, undefined, today, false).subscribe({
      next: (res) => {
        this.companyEvents = (res?.data?.data || []).filter((e: any) => {
          if (e.private_event) return false;
          if (e.cancelled) return false;
          const onlyTaquilla = !this.allChannelsEnabled && this.selectedChannelIds.includes(2) && !this.selectedChannelIds.includes(1);
          if (onlyTaquilla) return !!e.only_tpv_sales;
          if (!this.allChannelsEnabled && this.selectedChannelIds.includes(1) && !this.selectedChannelIds.includes(2)) return !e.only_tpv_sales;
          return true;
        });
        // En edición: si el backend no devolvió session_ids, auto-seleccionar todas las sesiones de los eventos seleccionados
        if (this.isEdit && this.selectedSessionIds.length === 0) {
          this.selectedEventIds.forEach(eventId => this.addAllSessionsOfEvent(eventId));
        }
        this.applyFilters();
        this.loaded = true;
        this.eventsLoading = false;
        this.onSelectionChanged();
      },
      error: () => {
        this.loaded = true;
        this.eventsLoading = false;
      }
    });
  }

  onSearch() {
    this.applyFilters();
  }

  applyFilters() {
    let events = [...this.companyEvents];
    if (this.eventCategoryFilter) {
      events = events.filter(e => (e.category?.id || e.id_event_category) === this.eventCategoryFilter);
    }
    const term = this.searchTerm.toLowerCase().trim();
    if (term) {
      events = events.filter(e => e.title?.toLowerCase().includes(term));
    }
    this.filteredEvents = events;
  }

  // ===== Event selection =====
  isEventSelected(eventId: number): boolean {
    return this.selectedEventIds.includes(eventId);
  }

  toggleEvent(eventId: number) {
    const idx = this.selectedEventIds.indexOf(eventId);
    if (idx >= 0) {
      this.selectedEventIds.splice(idx, 1);
      this.removeAllSessionsOfEvent(eventId);
    } else {
      this.selectedEventIds.push(eventId);
      // Por defecto seleccionar solo la primera sesión del evento (mínimo 1)
      this.addFirstSessionOfEvent(eventId);
    }
    this.onSelectionChanged();
  }

  toggleSession(sessionId: number, eventId: number) {
    const idx = this.selectedSessionIds.indexOf(sessionId);
    if (idx >= 0) {
      // No permitir dejar el evento sin ninguna sesión
      const remaining = this.getSessionsForEvent(eventId)
        .filter(s => s.id !== sessionId && this.selectedSessionIds.includes(s.id));
      if (remaining.length === 0) return;
      this.selectedSessionIds.splice(idx, 1);
    } else {
      this.selectedSessionIds.push(sessionId);
    }
    this.onSelectionChanged();
  }

  // ===== Session helpers =====
  getSessionsForEvent(eventId: number): any[] {
    const event = this.companyEvents.find(e => e.id === eventId);
    return event?.sessions || [];
  }

  isSessionSelected(sessionId: number): boolean {
    return this.selectedSessionIds.includes(sessionId);
  }

  addAllSessionsOfEvent(eventId: number) {
    const sessions = this.getSessionsForEvent(eventId);
    sessions.forEach(s => {
      if (!this.selectedSessionIds.includes(s.id)) this.selectedSessionIds.push(s.id);
    });
  }

  addFirstSessionOfEvent(eventId: number) {
    const first = this.getSessionsForEvent(eventId)[0];
    if (first && !this.selectedSessionIds.includes(first.id)) {
      this.selectedSessionIds.push(first.id);
    }
  }

  removeAllSessionsOfEvent(eventId: number) {
    const sessionIds = this.getSessionsForEvent(eventId).map(s => s.id);
    if (!sessionIds.length) return;
    this.selectedSessionIds = this.selectedSessionIds.filter(id => !sessionIds.includes(id));
  }

  getSelectedSessionsForEvent(eventId: number): any[] {
    return this.getSessionsForEvent(eventId).filter(s => this.selectedSessionIds.includes(s.id));
  }

  getSelectedEventSessionPairs(): { event: any; session: any }[] {
    const pairs: { event: any; session: any }[] = [];
    this.getSelectedEvents().forEach(event => {
      this.getSelectedSessionsForEvent(event.id).forEach(session => pairs.push({ event, session }));
    });
    return pairs;
  }

  getSessionDisplayTime(session: any): string | null {
    if (session?.start_time) return session.start_time;
    const raw: string = session?.start_date;
    if (typeof raw === 'string' && raw.includes('T')) {
      const m = raw.match(/T(\d{2}:\d{2})/);
      if (m && m[1] !== '00:00') return m[1];
    }
    return null;
  }

  getEventDateLabel(event: any): string {
    const fmt = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const sessions: any[] = event?.sessions || [];
    if (sessions.length <= 1) {
      const raw = event?.start_date;
      return raw ? fmt(new Date(raw)) : '';
    }
    const sorted = [...sessions].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const first = new Date(sorted[0].start_date);
    const last = new Date(sorted[sorted.length - 1].start_date);
    const fmtShort = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
    return `${fmtShort(first)} – ${fmtShort(last)} · ${sessions.length} sesiones`;
  }

  getEventDisplayTime(event: any): string | null {
    if (event?.start_time) return event.start_time;
    const first = event?.sessions?.[0];
    if (first?.start_time) return first.start_time;
    const raw: string = first?.start_date || event?.start_date;
    if (typeof raw === 'string' && raw.includes('T')) {
      const m = raw.match(/T(\d{2}:\d{2})/);
      if (m && m[1] !== '00:00') return m[1];
    }
    return null;
  }

  removeSessionChip(sessionId: number, eventId: number) {
    this.selectedSessionIds = this.selectedSessionIds.filter(id => id !== sessionId);
    const remaining = this.getSessionsForEvent(eventId).filter(s => this.selectedSessionIds.includes(s.id));
    if (remaining.length === 0) {
      this.selectedEventIds = this.selectedEventIds.filter(id => id !== eventId);
    }
    this.onSelectionChanged();
  }

  removeEvent(eventId: number) {
    this.selectedEventIds = this.selectedEventIds.filter(id => id !== eventId);
    this.removeAllSessionsOfEvent(eventId);
    this.onSelectionChanged();
  }

  getSelectedEvents(): any[] {
    return this.companyEvents.filter(e => this.selectedEventIds.includes(e.id));
  }

  getFilteredEvents(): any[] {
    return this.filteredEvents;
  }

  trackByEventId(_index: number, event: any): number {
    return event.id;
  }

  trackByPair(_index: number, pair: { event: any; session: any }): string {
    return `${pair.event.id}-${pair.session.id}`;
  }

  loadCategories() {
    this.categoriasService.getAllCategories().subscribe({
      next: (res) => {
        this.eventCategories = res?.data?.data || [];
      }
    });
  }

  selectCategory(catId: number | null) {
    this.eventCategoryFilter = catId;
    this.categoryDropdownOpen = false;
    this.applyFilters();
  }

  getSelectedCategoryName(): string {
    if (!this.eventCategoryFilter) return 'Todas las categorías';
    const cat = this.eventCategories.find(c => c.id === this.eventCategoryFilter);
    return cat?.name || 'Categoría';
  }

  getSelectedCategoryIcon(): string {
    if (!this.eventCategoryFilter) return 'fa-grid-2';
    const cat = this.eventCategories.find(c => c.id === this.eventCategoryFilter);
    return cat?.icon || 'fa-tag';
  }

  getCategoryIcon(categoryId: number): string {
    const cat = this.eventCategories.find(c => c.id === categoryId);
    return cat?.icon || 'fa-tag';
  }

  // ===== Butaca única =====
  setBondType(type: 'fixed' | 'flexible') {
    // Un pack flexible nunca es de butaca única ni de precio fijo
    this.form.patchValue(type === 'flexible'
      ? { bond_type: type, single_seat: false, fixed_price: false }
      : { bond_type: type });
    this.updateDiscountValidators();
    this.onSelectionChanged();
  }

  // ===== Precio por evento y nivel (fixed_price) =====
  get isFixedPrices(): boolean {
    return this.form.get('bond_type')?.value === 'fixed' && !!this.form.get('fixed_price')?.value;
  }

  setFixedPrice(fixed: boolean) {
    this.form.patchValue({ fixed_price: fixed });
    this.updateDiscountValidators();
    this.onSelectionChanged();
  }

  /** El % solo se pide en el modo de descuento: con precio fijo no existe */
  private updateDiscountValidators() {
    const control = this.form.get('discount_value');
    control?.setValidators(this.isFixedPrices ? [] : [Validators.required, Validators.min(0), Validators.max(100)]);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  /** Con precio fijo, cada cambio de eventos o sesiones vuelve a pedir la rejilla */
  onSelectionChanged() {
    if (this.isFixedPrices && this.loaded) {
      this.loadPassGrid();
    }
  }

  private buildPassItems(): { event_id: number; session_ids: number[] }[] {
    return this.selectedEventIds.map(eventId => ({
      event_id: eventId,
      session_ids: this.getSelectedSessionsForEvent(eventId).map(s => s.id),
    }));
  }

  /**
   * Pide al back una fila por sesión con los niveles de su versión de mapa (y,
   * al editar, los precios guardados). Lo ya escrito en el formulario manda sobre
   * lo guardado; solo se quitan las sesiones o niveles que dejan de estar.
   */
  loadPassGrid() {
    const passItems = this.buildPassItems();
    const key = JSON.stringify(passItems);
    if (key === this.gridRequestKey) return;
    this.gridRequestKey = key;
    this.gridSub?.unsubscribe();

    if (passItems.length === 0) {
      this.passGrid = null;
      this.priceCells = {};
      return;
    }

    this.gridLoading = true;
    this.gridError = null;
    this.gridSub = this.bondsService.getPassGrid(passItems, this.isEdit ? this.bondId : null).subscribe({
      next: (res) => {
        const grid = this.buildGrid(res?.sessions || []);
        const cells: { [sessionId: number]: { [columnKey: string]: number | null } } = {};

        grid.rows.forEach((row: any) => {
          cells[row.session_id] = {};
          Object.keys(row.cells).forEach(columnKey => {
            const typed = this.priceCells[row.session_id];
            cells[row.session_id][columnKey] = typed && Object.prototype.hasOwnProperty.call(typed, columnKey)
              ? typed[columnKey]
              : row.cells[columnKey].saved;
          });
        });

        this.priceCells = cells;
        this.passGrid = grid;
        this.gridLoading = false;
      },
      error: (err: any) => {
        this.gridLoading = false;
        this.gridRequestKey = null;
        this.gridError = err?.error?.message || 'No se han podido cargar los niveles de los eventos.';
      }
    });
  }

  /**
   * Columnas por zona: las sesiones pueden estar en versiones distintas del mapa
   * (cada una con sus `map_levels.id`), así que se emparejan por nombre de nivel.
   * Los eventos sin mapa van a una columna propia de precio único.
   */
  private buildGrid(sessions: any[]): { columns: any[]; rows: any[] } {
    const columns = new Map<string, { key: string; name: string; order: number }>();
    const rows = sessions.map((session: any) => {
      const cells: { [key: string]: { level_id: number | null; saved: number | null } } = {};
      if (session.uses_map) {
        (session.levels || []).forEach((level: any) => {
          const key = 'level:' + `${level.name || level.level_id}`.trim().toLowerCase();
          const order = level.order_buy ?? 999;
          const column = columns.get(key);
          if (!column) columns.set(key, { key, name: level.name, order });
          else column.order = Math.min(column.order, order);
          cells[key] = { level_id: level.level_id, saved: level.price != null ? +level.price : null };
        });
      } else {
        columns.set('no_map', { key: 'no_map', name: 'Precio único', order: 10000 });
        cells['no_map'] = { level_id: null, saved: session.price != null ? +session.price : null };
      }
      return { session_id: session.session_id, event_id: session.event_id, title: session.title, start_date: session.start_date, cells };
    });

    return { columns: [...columns.values()].sort((a, b) => a.order - b.order), rows };
  }

  getGridCell(row: any, columnKey: string): any | null {
    return row?.cells?.[columnKey] ?? null;
  }

  setCellPrice(sessionId: number, columnKey: string, value: any) {
    const price = value === '' || value === null || value === undefined ? null : +value;
    if (!this.priceCells[sessionId]) this.priceCells[sessionId] = {};
    this.priceCells[sessionId][columnKey] = price != null && !isNaN(price) && price >= 0 ? price : null;
  }

  /** Pone el precio de "Rellenar" en todas las celdas, en una fila o en una columna (vacío = las vacía) */
  fillPrices(target: { sessionId?: number; columnKey?: string } = {}) {
    if (!this.passGrid) return;
    const price = this.bulkPrice != null && `${this.bulkPrice}` !== '' ? +this.bulkPrice : null;

    this.passGrid.rows.forEach((row: any) => {
      if (target.sessionId != null && row.session_id !== target.sessionId) return;
      this.passGrid!.columns.forEach((column: any) => {
        if (target.columnKey != null && column.key !== target.columnKey) return;
        if (!this.getGridCell(row, column.key)) return;
        this.setCellPrice(row.session_id, column.key, price);
      });
    });
  }

  /**
   * Filas de la rejilla por evento: de un evento con varias sesiones a elegir el
   * comprador se lleva una, así que para los totales cuenta la más barata (igual
   * que el `price_summary` del back).
   */
  private rowsByEvent(): any[][] {
    const groups = new Map<number, any[]>();
    (this.passGrid?.rows || []).forEach((row: any) => {
      if (!groups.has(row.event_id)) groups.set(row.event_id, []);
      groups.get(row.event_id)!.push(row);
    });
    return [...groups.values()];
  }

  /** Total del abono en una zona, si esa zona tiene precio en todos los eventos */
  getColumnTotal(columnKey: string): number | null {
    const groups = this.rowsByEvent();
    if (!groups.length) return null;
    let total = 0;
    for (const rows of groups) {
      const prices = rows.map(row => this.priceCells[row.session_id]?.[columnKey]).filter(p => p != null) as number[];
      if (prices.length === 0) return null;
      total += Math.min(...prices);
    }
    return total;
  }

  /** Lo más barato que puede costar el abono: la zona más barata de cada evento */
  getFromTotal(): number | null {
    const groups = this.rowsByEvent();
    if (!groups.length) return null;
    let total = 0;
    for (const rows of groups) {
      const prices = rows.flatMap(row => Object.values(this.priceCells[row.session_id] || {})).filter(p => p != null) as number[];
      if (prices.length === 0) return null;
      total += Math.min(...prices);
    }
    return total;
  }

  /**
   * Lo que impide guardar la rejilla. El back vuelve a validarlo todo; aquí se
   * adelanta para no dejar pulsar Guardar en balde.
   */
  getPriceIssues(): string[] {
    if (!this.isFixedPrices || this.selectedEventIds.length === 0) return [];
    if (this.gridLoading || !this.passGrid) return [this.gridError || 'Cargando los niveles de los eventos…'];

    const issues: string[] = [];
    this.passGrid.rows.forEach((row: any) => {
      const prices = Object.values(this.priceCells[row.session_id] || {}).filter(p => p != null);
      if (prices.length === 0) {
        const date = row.start_date ? formatDate(row.start_date, 'dd/MM HH:mm', 'es', '+0000') : '';
        issues.push(`«${row.title}»${date ? ' (' + date + ')' : ''} no tiene ningún precio.`);
      }
    });
    return issues;
  }

  /**
   * Celdas para el back: todos los niveles de cada sesión, con precio o con null
   * para dejarlos fuera del abono.
   */
  private buildFixedPrices(): { session_id: number; level_id: number | null; price: number | null }[] {
    const prices: { session_id: number; level_id: number | null; price: number | null }[] = [];
    (this.passGrid?.rows || []).forEach((row: any) => {
      Object.keys(row.cells).forEach(columnKey => {
        prices.push({
          session_id: row.session_id,
          level_id: row.cells[columnKey].level_id,
          price: this.priceCells[row.session_id]?.[columnKey] ?? null,
        });
      });
    });
    return prices;
  }

  trackByColumnKey(_index: number, column: any): string {
    return column.key;
  }

  trackByGridRow(_index: number, row: any): number {
    return row.session_id;
  }

  get isSingleSeat(): boolean {
    return this.form.get('bond_type')?.value === 'fixed' && !!this.form.get('single_seat')?.value;
  }

  /**
   * Eventos elegidos que impiden que el abono sea de butaca única: todos
   * tienen que usar mapa, estar en el mismo espacio y llevar una sola sesión.
   * Es la misma regla que valida el back al guardar (que es quien manda); aquí
   * se adelanta para avisar mientras se eligen los eventos.
   */
  getSingleSeatIssues(): { title: string; message: string }[] {
    if (!this.isSingleSeat) return [];

    const events = this.getSelectedEvents();
    const spaceOf = (e: any) => e.id_space ?? e.space?.id ?? null;

    const spaceCount = new Map<any, number>();
    events.filter(e => e.use_map !== false && spaceOf(e) != null)
      .forEach(e => spaceCount.set(spaceOf(e), (spaceCount.get(spaceOf(e)) || 0) + 1));
    const mainSpace = [...spaceCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

    const issues: { title: string; message: string }[] = [];
    events.forEach(e => {
      if (e.use_map === false) {
        issues.push({ title: e.title, message: 'no usa mapa de butacas' });
      } else if (mainSpace != null && spaceOf(e) != null && spaceOf(e) !== mainSpace) {
        issues.push({ title: e.title, message: 'está en otro espacio' });
      } else if (this.getSelectedSessionsForEvent(e.id).length > 1) {
        issues.push({ title: e.title, message: 'tiene más de una sesión elegida: deja solo una' });
      }
    });
    return issues;
  }

  save() {
    if (this.form.invalid || this.saving || this.selectedEventIds.length === 0) return;
    if (this.getSingleSeatIssues().length > 0) return;
    if (this.getPriceIssues().length > 0) return;

    // Cada evento seleccionado debe tener al menos una sesión
    const invalid = this.selectedEventIds.some(eventId => {
      const sessions = this.getSessionsForEvent(eventId);
      return sessions.length > 0 && this.getSelectedSessionsForEvent(eventId).length === 0;
    });
    if (invalid) return;

    this.saving = true;
    this.saveError = null;

    const pass_items = this.buildPassItems();

    const payload: any = {
      name: this.form.value.name,
      rule_type: 'pass',
      id_company: this.business.id,
      apply_scope: 'company',
      session_strict_mode: false,
      active: true,
      allowed_channels: this.allowedChannels,
      pass_items,
      single_seat: this.isSingleSeat,
    };

    // Con precio fijo no hay %: el back crea la entrada del abono en cada sesión con estos precios
    if (this.isFixedPrices) {
      payload.fixed_price = true;
      payload.fixed_prices = this.buildFixedPrices();
      payload.min_events = null;
    } else {
      payload.fixed_price = false;
      payload.discount_mode = 'percentage';
      payload.discount_value = this.form.value.discount_value;
    }

    // Un abono de butaca única tiene que quedar sin mínimo de eventos aunque
    // antes fuera un pack flexible: el back lo rechaza si conserva `min_events`.
    if (this.isSingleSeat) {
      payload.min_events = null;
    }

    if (this.form.value.bond_type === 'flexible' && this.form.value.min_events) {
      payload.min_events = this.form.value.min_events;
    }

    if (this.form.value.starts_at) {
      payload.starts_at = this.form.value.starts_at;
    }
    if (this.form.value.ends_at) {
      payload.ends_at = this.form.value.ends_at;
    }

    const request$ = this.isEdit
      ? this.bondsService.editBond(this.bondId, payload)
      : this.bondsService.createBond(payload);

    request$.subscribe({
      next: () => {
        this.router.navigate(['/panel-control/bonds']);
      },
      error: (err: any) => {
        this.saving = false;
        // Precio fijo: el back da el motivo concreto en `errors`, el `message` es genérico
        const fixedPriceErrors = err?.error?.reason === 'fixed_prices_invalid' && Array.isArray(err?.error?.errors)
          ? err.error.errors.join(' ')
          : null;
        this.saveError = fixedPriceErrors || err?.error?.message || 'No se ha podido guardar el abono. Revisa los datos e inténtalo de nuevo.';
      }
    });
  }

  cancel() {
    this.router.navigate(['/panel-control/bonds']);
  }

  onStartsAtChange(date: Date) {
    if (date) {
      this.startsAtDate = date;
      this.form.patchValue({ starts_at: formatDate(date, 'yyyy-MM-ddTHH:mm', 'es') });
    } else {
      this.startsAtDate = null;
      this.form.patchValue({ starts_at: null });
    }
  }

  onEndsAtChange(date: Date) {
    if (date) {
      this.endsAtDate = date;
      this.form.patchValue({ ends_at: formatDate(date, 'yyyy-MM-ddTHH:mm', 'es') });
    } else {
      this.endsAtDate = null;
      this.form.patchValue({ ends_at: null });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.businessSub?.unsubscribe();
    this.gridSub?.unsubscribe();
    this.panelTitleService.setSubtitle(null);
  }
}
